import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Radio } from '@kontentino/ui';
import {
  AssetTypeFilter,
  UploadedByFilter,
} from 'app/modules/mediaLibraryPage/types/AssetFilters';
import {
  SortDirection,
  SortField,
  SortOption,
} from 'app/modules/mediaLibraryPage/types/SortOption';

const TYPE_OPTIONS: { value: AssetTypeFilter | undefined; labelKey: string }[] =
  [
    { value: undefined, labelKey: 'all' },
    { value: 'images', labelKey: 'mediaLibrary.page.toolbar.type.image' },
    { value: 'videos', labelKey: 'mediaLibrary.page.toolbar.type.video' },
    { value: 'gifs', labelKey: 'mediaLibrary.page.toolbar.type.gif' },
    { value: 'documents', labelKey: 'mediaLibrary.page.toolbar.type.document' },
  ];

const UPLOADED_BY_OPTIONS: {
  value: UploadedByFilter | undefined;
  labelKey: string;
  dataName: string;
}[] = [
  {
    value: 'me',
    labelKey: 'mediaLibrary.page.toolbar.uploadedBy.me',
    dataName: 'me',
  },
  {
    value: undefined,
    labelKey: 'mediaLibrary.page.toolbar.uploadedBy.anyone',
    dataName: 'anyone',
  },
];

const SORT_FIELD_OPTIONS: { value: SortField; labelKey: string }[] = [
  { value: 'created_at', labelKey: 'mediaLibrary.page.toolbar.sort.newest' },
  { value: 'name', labelKey: 'mediaLibrary.page.toolbar.sort.name' },
  { value: 'file_size', labelKey: 'mediaLibrary.page.toolbar.sort.fileSize' },
];

const SORT_DIRECTION_OPTIONS: { value: SortDirection; labelKey: string }[] = [
  { value: 'desc', labelKey: 'mediaLibrary.page.toolbar.sort.descending' },
  { value: 'asc', labelKey: 'mediaLibrary.page.toolbar.sort.ascending' },
];

interface Props {
  type: AssetTypeFilter | undefined;
  uploadedBy: UploadedByFilter | undefined;
  sort: SortOption;
  onChangeType(next: AssetTypeFilter | undefined): void;
  onChangeUploadedBy(next: UploadedByFilter | undefined): void;
  onChangeSort(field: SortField, direction?: SortDirection): void;
}

const FilterSection: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <div className="tw-mt-4">
    <Field.Label required className="tw-mb-2">
      {title}
    </Field.Label>
    <div className="tw-flex tw-flex-col tw-gap-1">{children}</div>
  </div>
);

const RadioRow: FC<{
  name: string;
  label: string;
  isChecked: boolean;
  onSelect(): void;
  dataCy: string;
}> = ({ name, label, isChecked, onSelect, dataCy }) => (
  <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-py-1 tw-text-sm tw-text-grayscale-180">
    <Radio
      size="small"
      name={name}
      checked={isChecked}
      onChange={onSelect}
      data-cy={dataCy}
    />
    <span className="tw-truncate">{label}</span>
  </label>
);

const MediaLibraryPickerFilters: FC<Props> = ({
  type,
  uploadedBy,
  sort,
  onChangeType,
  onChangeUploadedBy,
  onChangeSort,
}) => {
  const { t } = useTranslation();

  return (
    <div data-cy="media-library-picker_filters">
      <FilterSection title={t('mediaLibrary.page.toolbar.type.all')}>
        {TYPE_OPTIONS.map((option) => (
          <RadioRow
            key={`type-${option.value ?? 'all'}`}
            name="media-library-picker-type"
            dataCy={`media-library-picker_filters-type-${
              option.value ?? 'all'
            }`}
            label={t(option.labelKey)}
            isChecked={type === option.value}
            onSelect={() => onChangeType(option.value)}
          />
        ))}
      </FilterSection>

      <FilterSection title={t('mediaLibrary.page.toolbar.uploadedBy.all')}>
        {UPLOADED_BY_OPTIONS.map((option) => (
          <RadioRow
            key={`uploaded-by-${option.dataName}`}
            name="media-library-picker-uploaded-by"
            dataCy={`media-library-picker_filters-uploaded-by-${option.dataName}`}
            label={t(option.labelKey)}
            isChecked={uploadedBy === option.value}
            onSelect={() => onChangeUploadedBy(option.value)}
          />
        ))}
      </FilterSection>

      <FilterSection title={t('mediaLibrary.page.toolbar.sortBy')}>
        {SORT_FIELD_OPTIONS.map((option) => (
          <RadioRow
            key={`sort-field-${option.value}`}
            name="media-library-picker-sort"
            dataCy={`media-library-picker_filters-sort-${option.value}`}
            label={t(option.labelKey)}
            isChecked={sort.by === option.value}
            onSelect={() => onChangeSort(option.value, sort.direction)}
          />
        ))}
      </FilterSection>

      <FilterSection title={t('mediaLibrary.page.toolbar.order')}>
        {SORT_DIRECTION_OPTIONS.map((option) => (
          <RadioRow
            key={`sort-direction-${option.value}`}
            name="media-library-picker-sort-direction"
            dataCy={`media-library-picker_filters-sort-direction-${option.value}`}
            label={t(option.labelKey)}
            isChecked={sort.direction === option.value}
            onSelect={() => onChangeSort(sort.by, option.value)}
          />
        ))}
      </FilterSection>
    </div>
  );
};

export default MediaLibraryPickerFilters;
